import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { recipesAPI, favoritesAPI, ratingsAPI } from '../lib/api';
import { logger } from '../lib/logger';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { DifficultyStars } from '../components/ui/DifficultyStars';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { CommentsSection } from '../components/recipes/CommentsSection';
import { ShareMenu } from '../components/recipes/ShareMenu';
import { toast } from 'sonner';
import {
  ChevronLeft, Clock, Users, ChefHat, Heart, Sparkles, Star
} from 'lucide-react';

const MEAL_TYPE_LABELS = {
  entree: 'Entrée',
  plat: 'Plat principal',
  dessert: 'Dessert',
};

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1699572583203-97170ae281c0?w=800&q=80',
  'https://images.unsplash.com/photo-1673553518378-34d8186e57b6?w=800&q=80',
  'https://images.unsplash.com/photo-1711633648854-50a30a6df74d?w=800&q=80',
];

export default function RecipeDetailPage() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [checkedIngredients, setCheckedIngredients] = useState({});

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const response = await recipesAPI.getById(recipeId);
        setRecipe(response.data);

        if (isAuthenticated) {
          try {
            const [favRes, ratingRes] = await Promise.all([
              favoritesAPI.check(recipeId),
              ratingsAPI.get(recipeId),
            ]);
            setIsFavorite(favRes.data.is_favorite);
            setUserRating(ratingRes.data.user_rating || 0);
          } catch (e) {
            // Auth-only fetches (favorite check, rating). Token may have expired between
            // recipe fetch and these calls — fail silently but log for debugging.
            logger.debug('Auth-related fetch skipped:', e?.message);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch recipe:', error);
        toast.error('Recette non trouvée');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [recipeId, isAuthenticated, navigate]);

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      if (isFavorite) {
        await favoritesAPI.remove(recipeId);
        setIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await favoritesAPI.add(recipeId);
        setIsFavorite(true);
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleRating = async (rating) => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour noter');
      return;
    }

    try {
      await ratingsAPI.rate({ recipe_id: recipeId, rating });
      setUserRating(rating);
      toast.success('Note enregistrée');
      // Refresh recipe to get updated average
      const response = await recipesAPI.getById(recipeId);
      setRecipe(response.data);
    } catch (error) {
      toast.error('Erreur lors de la notation');
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-worldplate-cream">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-stone-200 rounded w-1/4" />
            <div className="aspect-video bg-stone-200 rounded-2xl" />
            <div className="h-6 bg-stone-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!recipe) return null;

  const imageUrl = recipe.image_url || PLACEHOLDER_IMAGES[0];
  const totalTime = recipe.prep_time + recipe.cook_time;

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-worldplate-muted hover:text-worldplate-text mb-6 -ml-2"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Retour
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left - Image & Actions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:sticky lg:top-32 lg:self-start space-y-6"
          >
            {/* Image */}
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
              <img
                src={imageUrl}
                alt={recipe.name}
                className="w-full h-full object-cover"
              />
              {recipe.is_ai_generated && (
                <div className="absolute top-4 left-4">
                  <Badge className="bg-purple-500 text-white flex items-center gap-1 px-3 py-1">
                    <Sparkles className="w-4 h-4" />
                    Générée par IA
                  </Badge>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleFavorite}
                variant={isFavorite ? 'default' : 'outline'}
                className={`flex-1 gap-2 ${isFavorite ? 'bg-worldplate-primary hover:bg-worldplate-primary/90' : ''}`}
                data-testid="favorite-btn"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'Dans vos favoris' : 'Ajouter aux favoris'}
              </Button>
              <ShareMenu
                title={recipe.name}
                description={recipe.description}
                url={window.location.href}
                image={recipe.image_url}
                buttonVariant="outline"
              />
            </div>

            {/* Rating */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <h3 className="font-heading text-lg font-semibold text-worldplate-deep mb-4">
                Notez cette recette
              </h3>
              <div className="flex items-center gap-4">
                <DifficultyStars
                  difficulty={userRating}
                  interactive={isAuthenticated}
                  onChange={handleRating}
                  size="lg"
                />
                {recipe.rating_count > 0 && (
                  <span className="text-worldplate-muted text-sm">
                    {recipe.average_rating.toFixed(1)} ({recipe.rating_count} avis)
                  </span>
                )}
              </div>
              {!isAuthenticated && (
                <p className="text-sm text-worldplate-muted mt-3">
                  <Link to="/login" className="text-worldplate-primary hover:underline">
                    Connectez-vous
                  </Link>{' '}
                  pour noter cette recette
                </p>
              )}
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Header */}
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary" className="bg-worldplate-secondary text-white">
                  {recipe.country}
                </Badge>
                <Badge variant="secondary" className="bg-worldplate-soft text-worldplate-deep">
                  {MEAL_TYPE_LABELS[recipe.meal_type] || recipe.meal_type}
                </Badge>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-worldplate-deep mb-4" data-testid="recipe-title">
                {recipe.name}
              </h1>
              <p className="text-lg text-worldplate-muted leading-relaxed">
                {recipe.description}
              </p>
            </div>

            {/* Meta */}
            <div className="flex flex-wrap gap-6 py-6 border-y border-stone-200">
              <div className="flex items-center gap-2 text-worldplate-text">
                <Clock className="w-5 h-5 text-worldplate-primary" />
                <div>
                  <p className="font-semibold">{totalTime} min</p>
                  <p className="text-sm text-worldplate-muted">
                    {recipe.prep_time} prep + {recipe.cook_time} cuisson
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-worldplate-text">
                <Users className="w-5 h-5 text-worldplate-primary" />
                <div>
                  <p className="font-semibold">{recipe.servings} personnes</p>
                  <p className="text-sm text-worldplate-muted">Portions</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-worldplate-text">
                <Star className="w-5 h-5 text-worldplate-primary" />
                <div>
                  <p className="font-semibold">Niveau {recipe.difficulty}/5</p>
                  <p className="text-sm text-worldplate-muted">Difficulté</p>
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="ingredients-section">
              <h2 className="font-heading text-2xl font-bold text-worldplate-deep mb-6 flex items-center gap-2">
                <ChefHat className="w-6 h-6 text-worldplate-primary" />
                Ingrédients
              </h2>
              <ul className="space-y-3">
                {recipe.ingredients.map((ing, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Checkbox
                      id={`ing-${index}`}
                      checked={checkedIngredients[index] || false}
                      onCheckedChange={() => toggleIngredient(index)}
                      data-testid={`ingredient-checkbox-${index}`}
                    />
                    <label
                      htmlFor={`ing-${index}`}
                      className={`flex-1 cursor-pointer ${checkedIngredients[index] ? 'line-through text-worldplate-muted' : 'text-worldplate-text'}`}
                    >
                      <span className="font-medium">{ing.quantity} {ing.unit}</span>{' '}
                      {ing.name}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Instructions */}
            <div data-testid="instructions-section">
              <h2 className="font-heading text-2xl font-bold text-worldplate-deep mb-6">
                Préparation
              </h2>
              <ol className="space-y-6">
                {recipe.instructions.map((step, index) => (
                  <li key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-worldplate-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>
                    <p className="text-worldplate-text leading-relaxed pt-2">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <div className="pt-6 border-t border-stone-200">
                <h3 className="text-sm font-medium text-worldplate-muted mb-3 uppercase tracking-wide">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1 bg-worldplate-subtle text-worldplate-muted rounded-full text-sm hover:bg-worldplate-primary hover:text-white transition-colors tag-pill"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Author */}
            <div className="pt-6 border-t border-stone-200 text-sm text-worldplate-muted">
              Recette ajoutée par <span className="font-medium text-worldplate-text">{recipe.author_name}</span>
            </div>

            {/* Comments */}
            <CommentsSection recipeId={recipe.id} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
